import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

// Hooks
import { useUserFlag } from 'Shared/utils/hooks/use-user-flag';

// Utils
import { getCurrentMoment, convertMomentToUnix } from 'Shared/utils/DateTime.utils';

// Actions
import * as UsersActions from 'Shared/actions/Users.actions';

// Components
import WpBackdrop from 'Wpds/components/backdrop/WpBackdrop';
import { WpContainer, WpContainerBody } from 'Wpds/components/container';
import { WpButton } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpSplitProgressBar from 'Wpds/components/progress/WpSplitProgressBar';

// Styles
import './AgendaBuildGuide.scss';

type Props = {
  guideStep;
  setGuideStep: Function;
};

const AgendaBuildGuide = ({ guideStep, setGuideStep }: Props) => {
  const dispatch = useDispatch();
  const hasSeenAgendaBuilderGuide = useUserFlag({ flag: 'completed_workspace_agenda_builder_guide' });

  const steps = [
    {
      stepNumber: 0,
      showInPorgressBar: true,
      title: 'This is a section',
      description: 'Sections help you group discussion topics. They are persistent from meeting to meeting.',
    },
    {
      stepNumber: 1,
      showInPorgressBar: true,
      title: 'This lil thing is a discussion topic',
      description:
        'Topics are less permanent and vary meeting-to-meeting. We recommend you complete them during your meeting. Completed topics will be recorded in your Meeting Notes History.',
    },
    // {
    //   stepNumber: 2,
    //   showInPorgressBar: true,
    //   title: 'This topic is set to be revisited',
    //   description:
    //     'Set topics to a schedule that works for you. After you mark them as complete, they’ll pop back into the agenda when it’s time.',
    // },
    {
      stepNumber: 2,
      showInPorgressBar: false,
      title: 'That’s it',
      description: 'When you’re done customizing your agenda, you can apply it to any 1:1 workspace you’d like.',
      buttonText: 'Let’s do this!',
    },
  ];

  const stepsForProgressBar = steps.filter(step => step.showInPorgressBar);
  const step = steps.find(step => step.stepNumber === guideStep);
  const isLastStep = guideStep === steps.length - 1;

  const handleTrackComplete = () => {
    const timestamp = convertMomentToUnix(getCurrentMoment());
    dispatch(UsersActions.updateUserFlag('completed_workspace_agenda_builder_guide', timestamp));
  };

  const handleStepChange = () => {
    if (guideStep === steps.length - 2) handleTrackComplete();

    if (guideStep === steps.length - 1) {
      setGuideStep(-1);
    } else {
      setGuideStep(guideStep + 1);
    }
  };

  const hideGuideStep = () => {
    handleTrackComplete();
    setGuideStep(-1);
  };

  useEffect(() => {
    if (hasSeenAgendaBuilderGuide) setGuideStep(-1);
  }, []);

  if (guideStep === -1) return null;

  const baseClassName = 'AgendaBuildGuide';

  return (
    <>
      <WpContainer className={baseClassName}>
        <WpContainerBody>
          <div className={`${baseClassName}__header`}>
            <h5>{step.title}</h5>
            <div className={`${baseClassName}__header__close-btn`}>
              <WpButton variant="tertiary" size="sm" rounded onClick={hideGuideStep}>
                <WpIcon name="times" />
              </WpButton>
            </div>
          </div>
          <p>{step.description}</p>
          <WpButton onClick={handleStepChange}>{step.buttonText ? step.buttonText : 'Got it, next'}</WpButton>
          {!isLastStep && (
            <>
              <div className={`${baseClassName}__progress-bar`}>
                <WpSplitProgressBar steps={stepsForProgressBar} currentNumberStep={guideStep} />
              </div>
            </>
          )}
        </WpContainerBody>
      </WpContainer>
      <WpBackdrop />
    </>
  );
};

export default AgendaBuildGuide;
